import { IContextualMenuItem } from '@fluentui/react/lib/ContextualMenu';
import { assertNever } from 'owa-assert';
import { AttachmentFullViewState } from 'owa-attachment-full-data';
import { AttachmentModel } from 'owa-attachment-model-store';
import { PreviewContext } from 'owa-attachment-select-actions';
import AttachmentType from 'owa-service/lib/contract/AttachmentType';
import ItemId from 'owa-service/lib/contract/ItemId';
import ReferenceAttachment from 'owa-service/lib/contract/ReferenceAttachment';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import isConsumer from 'owa-session-store/lib/utils/isConsumer';
import addCalendarEventsAttachmentContextItemBuilder from './addCalendarEventsAttachmentContextItemBuilder';
import AttachmentContextMenuItemBuilder from './AttachmentContextMenuItemBuilder';
import changePermissionsContextMenuItemBuilder from './changePermissionsContextMenuItemBuilder';
import convertClassicToCloudyContextMenuItemBuilder from './convertClassicToCloudyContextMenuItemBuilder';
import convertCloudyToClassicContextMenuItemBuilder from './convertCloudyToClassicContextMenuItemBuilder';
import copyLinkContextMenuItemBuilder from './copyLinkContextMenuItemBuilder';
import downloadContextMenuItemBuilder from './DownloadContextMenuItemBuilder';
import moveAttachmentToInlineContextMenuItemBuilder from './moveAttachmentToInlineContextMenuItemBuilder';
import openInNewTabContextMenuItemBuilder from './openInNewTabContextMenuItemBuilder';
import previewContextMenuItemBuilder from './previewContextMenuItemBuilder';
import refreshLinkContextMenuItemBuilder from './refreshLinkContextMenuItemBuilder';
import saveToCloudContextMenuItemBuilder from './saveToCloudContextMenuItemBuilder';
import viewInProviderContextMenuItemBuilder from './viewInProviderContextMenuItemBuilder';

export const enum AttachmentContextMenuItem {
    /// Option to change the permission on the attachment
    ChangePermission,

    /// Convert the classic attachment to cloudy attachment
    ConvertClassicToCloudy,

    /// Convert the cloudy attachment to local attachment
    ConvertCloudyToClassic,

    /// Copy the link of the cloudy attachment to clipboard
    CopyLinkToClipboard,

    /// Download the attachment
    Download,

    /// Import the calendar event to calendar
    ImportIcsAttachment,

    /// Move the attachment to inline
    MoveAttachmentToInline,

    /// Open the cloudy attachment in new tab
    OpenInNewTab,

    /// Preview the attachment (e.g. in WAC)
    Preview,

    /// Save the classic attachment in cloud
    SaveToCloud,

    /// View the attachment in the parent folder in provider
    ViewInProvider,

    // Refreshes a link that has expired or is going to expire
    RefreshLink,
}

function getContextMenuOptions(): AttachmentContextMenuItem[] {
    const contextMenuOptions = [
        AttachmentContextMenuItem.Preview,
        AttachmentContextMenuItem.ChangePermission,
        AttachmentContextMenuItem.Download,
        AttachmentContextMenuItem.ConvertCloudyToClassic,
        AttachmentContextMenuItem.OpenInNewTab,
        AttachmentContextMenuItem.CopyLinkToClipboard,
        AttachmentContextMenuItem.ViewInProvider,
        AttachmentContextMenuItem.ImportIcsAttachment,
        AttachmentContextMenuItem.MoveAttachmentToInline,
        AttachmentContextMenuItem.RefreshLink,
    ];

    if (!getUserConfiguration().SessionSettings.IsShadowMailbox) {
        contextMenuOptions.push(AttachmentContextMenuItem.ConvertClassicToCloudy);
        contextMenuOptions.push(AttachmentContextMenuItem.SaveToCloud);
    }
    return contextMenuOptions;
}

function createContextMenuItemBuilders(
    moveAttachmentToInlineHandler?: (attachment: AttachmentFullViewState, contentId: string) => void
): AttachmentContextMenuItemBuilder[] {
    const contextMenuOptions: AttachmentContextMenuItem[] = getContextMenuOptions();
    const contextMenuItemBuilders: AttachmentContextMenuItemBuilder[] = [];
    contextMenuOptions.forEach(contextMenu => {
        switch (contextMenu) {
            case AttachmentContextMenuItem.ChangePermission:
                contextMenuItemBuilders.push(changePermissionsContextMenuItemBuilder);
                break;
            case AttachmentContextMenuItem.Download:
                contextMenuItemBuilders.push(downloadContextMenuItemBuilder);
                break;
            case AttachmentContextMenuItem.SaveToCloud:
                contextMenuItemBuilders.push(saveToCloudContextMenuItemBuilder);
                break;
            case AttachmentContextMenuItem.CopyLinkToClipboard:
                contextMenuItemBuilders.push(copyLinkContextMenuItemBuilder);
                break;
            case AttachmentContextMenuItem.OpenInNewTab:
                contextMenuItemBuilders.push(openInNewTabContextMenuItemBuilder);
                break;
            case AttachmentContextMenuItem.ViewInProvider:
                contextMenuItemBuilders.push(viewInProviderContextMenuItemBuilder);
                break;
            case AttachmentContextMenuItem.Preview:
                contextMenuItemBuilders.push(previewContextMenuItemBuilder);
                break;
            case AttachmentContextMenuItem.ConvertClassicToCloudy:
                contextMenuItemBuilders.push(convertClassicToCloudyContextMenuItemBuilder);
                break;
            case AttachmentContextMenuItem.ConvertCloudyToClassic:
                contextMenuItemBuilders.push(convertCloudyToClassicContextMenuItemBuilder);
                break;
            case AttachmentContextMenuItem.ImportIcsAttachment:
                contextMenuItemBuilders.push(addCalendarEventsAttachmentContextItemBuilder);
                break;
            case AttachmentContextMenuItem.MoveAttachmentToInline:
                if (moveAttachmentToInlineHandler) {
                    contextMenuItemBuilders.push(moveAttachmentToInlineContextMenuItemBuilder);
                }
                break;
            case AttachmentContextMenuItem.RefreshLink:
                contextMenuItemBuilders.push(refreshLinkContextMenuItemBuilder);
                break;
            default:
                assertNever(contextMenu);
        }
    });
    return contextMenuItemBuilders;
}

export default function getContextMenus(
    attachmentViewState: AttachmentFullViewState,
    attachment: AttachmentModel,
    previewContext: PreviewContext,
    parentItemId: ItemId,
    onReplaceAttachment: (
        attachmentViewState: AttachmentFullViewState,
        attachmentType: AttachmentType,
        isReadOnly: boolean,
        uploadCompleted: boolean
    ) => void,
    container?: HTMLElement,
    moveAttachmentToInlineHandler?: (
        attachment: AttachmentFullViewState,
        contentId: string
    ) => void,
    referenceAttachmentsSupported?: () => boolean,
    targetWindow?: Window,
    sxsId?: string
): IContextualMenuItem[] {
    const contextMenuItemBuilds: AttachmentContextMenuItemBuilder[] = createContextMenuItemBuilders(
        moveAttachmentToInlineHandler
    );
    const contextMenuItems: IContextualMenuItem[] = [];
    contextMenuItemBuilds.forEach(contextMenuItemBuilder => {
        let permissionLevel = null;
        let providerType = null;
        let attachLongPathName = null;
        if (attachmentViewState.isCloudy) {
            const referenceAttachment: ReferenceAttachment = attachment.model as ReferenceAttachment;
            attachLongPathName = referenceAttachment.AttachLongPathName;
            providerType = referenceAttachment.ProviderType;
            permissionLevel = attachmentViewState.isReadOnly
                ? referenceAttachment.OriginalPermissionType
                : referenceAttachment.PermissionType;
        }

        const contextMenuItem = contextMenuItemBuilder(
            attachmentViewState,
            providerType,
            targetWindow,
            sxsId,
            isConsumer(),
            attachment.model.AttachmentOriginalUrl,
            permissionLevel,
            previewContext,
            parentItemId,
            onReplaceAttachment,
            attachLongPathName,
            container,
            moveAttachmentToInlineHandler,
            referenceAttachmentsSupported
        );
        if (contextMenuItem) {
            contextMenuItems.push(contextMenuItem);
        }
    });

    return contextMenuItems;
}
